import "@app/App.css";
import { FieldType, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_PERIOD_TYPE, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { visibleObjectActions } from "@dto/ObjectAction";
import { Period, PeriodType, PeriodTypeWeek, upperPeriodType } from "@dto/period";
import { Filter, OUTBOUND } from "@dto/records";
import { ExportRightOption, hasGlobalRightOption, ObjectRightType } from "@dto/Users";
import { getRelationDirection } from "@utils/model";
import { TW_ObjectModelViewHeader } from "@utils/twClasses";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import DeleteRowsButton from "./DeleteRowsButton";
import DuplicateRowsButton from "./DuplicateRowsButton";
import FilterButton from "./FilterButton";
import GroupButton from "./GroupButton";
import OrganizeFieldsButton from "./OrganizeFieldsButton";
import ImportButton from "./ImportButton";
import ExportButton from "./ExportButton";
import PeriodSelector from "./PeriodSelector";
import PivotButton from "./PivotButton";
import SearchInput from "./SearchInput";
import SortButton from "./SortButton";
import SummaryButton from "./SummaryButton";
import ShowAllLinesButton from "./ShowAllLinesButton";
import ObjectActionButton from "./ObjectActionButton";
import UndoButton from "./UndoButton";
import { SOURCE_SEPARATOR } from "@utils/constants";
import { Separator } from "@comps/ui/separator";


interface ObjectModelViewHeaderProps {
  searchText: string;
  setSearchText: (text: string) => void;
}

function ObjectModelViewHeader({ searchText, setSearchText }: ObjectModelViewHeaderProps) {

  const workspace = useModelStore(state => state.selectedWorkspace);
  const objectInfos = useModelStore(state => state.selectedObjectInfos);
  const view = useModelStore(state => state.selectedView);
  const rowSelection = useDataStore(state => state.rowSelection);
  const pivot = useDataStore(state => state.pivot);
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);
  const relationByKeys = useModelStore(state => state.relationByKeys);

  if (!workspace || !objectInfos) {
    return null;
  }

  // Collect relation filters from link fields
  const relationFilters: Filter[] = [];
  const treatedRelationKeys = new Set<string>();
  objectInfos.fields?.forEach(field => {
    if (field.type === FieldType.Link && field.typeItems) {
      const relationKey = field.typeItems.get(TYPE_ITEMS_RELATION_KEY);
      const destObjectKey = field.typeItems.get(TYPE_ITEMS_OBJECT_KEY);
      if (relationKey && destObjectKey) {
        if (treatedRelationKeys.has(relationKey)) {
          return;
        }
        treatedRelationKeys.add(relationKey);
        const relation = relationByKeys?.get(relationKey);
        if (relation) {
          const direction = getRelationDirection(relation, objectInfos.object._key);
          const directionStr = direction === OUTBOUND ? "OUTBOUND" : "INBOUND";
          // Get the filter based on the direction (we want the filter that applies to the destination object)
          const filter = direction === OUTBOUND ? relation.destFilter : relation.srcFilter;
          if (filter?.groupFilter?.filters) {
            // Transform pathKeys to include the relation path: ":relationKey-DIRECTION>originalPathKey"
            // Original pathKey starts with ':', we need to trim it and add it back at the beginning
            const transformedFilters = filter.groupFilter.filters.map(f => {
              if (f.groupFilter?.filters?.[0]?.singleFilter?.pathKey) {
                return {
                  ...f,
                  groupFilter: {
                    ...f.groupFilter,
                    filters: f.groupFilter.filters.map(innerF => {
                      if (innerF.singleFilter?.pathKey) {
                        const separatorIndex = innerF.singleFilter.pathKey.indexOf(SOURCE_SEPARATOR);
                        const originalPathKey = separatorIndex > -1
                          ? innerF.singleFilter.pathKey.substring(separatorIndex + 1)
                          : innerF.singleFilter.pathKey;
                        return {
                          ...innerF,
                          singleFilter: {
                            ...innerF.singleFilter,
                            pathKey: `:${relationKey}-${directionStr}>${originalPathKey}`
                          }
                        };
                      }
                      return innerF;
                    })
                  }
                };
              }
              return f;
            });
            relationFilters.push(...transformedFilters);
          }
        }
      }
    }
  });
  const hasRelationFilters = relationFilters.length > 0;

  const selectedRows = Object.keys(rowSelection).filter((key) => rowSelection[key]);
  const selectionStyle = (selectedRows.length > 0) ? {
    width: "max-content",
  } : {
    width: "0px",
    opacity: 0,
  }

  const hasDateOrPeriodPivot = pivot && (pivot.type === FieldType.Date || pivot.type === FieldType.Period);
  let initPeriod: Period | null = null;
  let periodType: PeriodType = PeriodTypeWeek;
  if (hasDateOrPeriodPivot) {
    if (pivot.type === FieldType.Period) {
      periodType = pivot.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE) as PeriodType || PeriodTypeWeek;
      periodType = upperPeriodType(periodType);
    }
    if (view?.period) {
      initPeriod = view.period;
    } else {
      initPeriod = Period.today().toPeriodType(periodType);
    }
  }

  // Without selection, only the actions always visible are shown
  const objectActions = visibleObjectActions(objectInfos?.actions || [], selectedRows.length > 0);

  // Owners can always export; other members need the EXPORT option of their
  // role on the object (the back-end applies the same rule).
  const canExport = user?.owner || hasGlobalRightOption(user, userRole, ObjectRightType, ExportRightOption, objectInfos?.object?._key);
  return (
    <header key={"object-model-view-header-" + objectInfos?.object._key + "-" + view?._key} className={TW_ObjectModelViewHeader}>
      <div className="flex flex-row grow">
        {selectedRows.length > 0 &&
          <>
            <div className="flex flex-row justify-start items-center gap-1 md:gap-4 w-max opacity-100" style={selectionStyle}>
              <DuplicateRowsButton />
              <DeleteRowsButton />
              {/* <Separator orientation="vertical" className="h-6 bg-gray-400 mr-4" /> */}
            </div>
            <div className="flex flex-row justify-end items-center gap-0 sm:gap-1 md:gap-2 lg:gap-4 grow pr-4">
              {objectActions.map((action) => (
                <ObjectActionButton key={action._key} action={action} />
              ))}
              {/* {objectActions.length > 0 && <Separator orientation="vertical" className="h-6 bg-gray-400 mr-4" />} */}
            </div>
          </>
        }
        {selectedRows.length === 0 && <div className="flex flex-row justify-start items-center gap-0 sm:gap-1 md:gap-2 lg:gap-4 grow">
          {!view && <OrganizeFieldsButton />}
          {!view && <Separator orientation="vertical" className="h-6 bg-gray-300" /> }
          {!view && <ShowAllLinesButton />}
          {!view && hasRelationFilters && <FilterButton relationFilters={relationFilters} allowNewFilters={false} />}
          {view &&
            <>
              <OrganizeFieldsButton />
              <FilterButton relationFilters={relationFilters} />
              <GroupButton />
              <SortButton />
              <PivotButton />
              <SummaryButton />
            </>
          }
        </div>}
        {selectedRows.length === 0 && objectActions.length > 0 &&
          <div className="flex flex-row justify-end items-center gap-0 sm:gap-1 md:gap-2 lg:gap-4 pr-4">
            {objectActions.map((action) => (
              <ObjectActionButton key={action._key} action={action} />
            ))}
          </div>
        }
        <UndoButton />
        <SearchInput value={searchText} onChange={setSearchText} />
        {!view && <ImportButton />}
        {canExport && !hasDateOrPeriodPivot && <ExportButton />}
      </div>
      {view && hasDateOrPeriodPivot && initPeriod &&
        <div className="flex flex-row justify-start items-center gap-1 md:gap-4 md:px-2 pb-1 md:pb-0">
          <PeriodSelector initPeriod={initPeriod} columnPeriodType={periodType} />
          {canExport && <ExportButton />}
        </div>
      }

    </header>
  );
}

export default ObjectModelViewHeader;
